import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Params } from '@angular/router';
import {
  LangChangeEvent,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { SvgPagePlaceholderComponent } from '@shared/components/svg-page-placeholder/svg-page-placeholder.component';
import { GroundColor } from '@shared/models/common.model';
import {
  MemoApproverStatus,
  MemoLoaPreview,
  MemoStatus,
} from '@shared/models/memo.model';
import { ThemeService } from '@shared/service/theme.service';
import { SharedModule } from '@shared/shared.module';
import {
  MemoApproverStatuses,
  MemoStatusColors,
  MemoStatuses,
  MemoStatusesTh,
  MemoStatusIconPaths,
} from '@shared/utils/memos.constant';
import { AngularSvgIconModule } from 'angular-svg-icon';
import * as _ from 'lodash';
import { UtilsService } from 'src/app/core/services/utils.service';
import { MemoService } from '../../../service/memo.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-loa-checkpoints',
  standalone: true,
  imports: [
    AngularSvgIconModule,
    CommonModule,
    FormsModule,
    TranslateModule,
    SharedModule,
    SvgPagePlaceholderComponent,
  ],
  templateUrl: './loa-checkpoints.component.html',
  styleUrls: ['./loa-checkpoints.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoaCheckpointsComponent
  implements OnChanges, OnInit, OnDestroy
{
  @Input() filters?: Params;
  @Input() loaStatusColors:
    | string
    | { [k in MemoStatus | string]?: Partial<GroundColor> } = 'theme';
  @Input() memoId: number;
  @Input() remindMemo: string;
  @Input() showDigitalSign = false;
  @Input() showBadge = true;
  @Input() showLevel = true;
  @Input() highlightApproveDate = true;
  @Output() typeReminder = new EventEmitter<string>();
  @Output() emitIndexLoaArray = new EventEmitter<string[]>();
  @Output() afterPreviewLoaFetch = new EventEmitter<
    MemoLoaPreview[]
  >();

  _loaStatusColors: {
    [k in MemoStatus | string]?: Partial<GroundColor>;
  };
  loaMemberIdArray: any = [];

  approverStatusColors: MemberStatusColor = {
    ...MemoStatusColors,
    canceled: {
      background: MemoStatusColors[MemoStatuses.Pending]?.background,
      foreground: MemoStatusColors[MemoStatuses.Pending]?.foreground,
    },
  };
  approverStatusBorderColors: {
    [k in MemoApproverStatus]?: string;
  } = {
    [MemoApproverStatuses.Approved]: '#34c759',
    [MemoApproverStatuses.Draft]: '#007aff',
    [MemoApproverStatuses.Expired]: '#876b4b',
    [MemoApproverStatuses.Pending]: '#007aff',
    [MemoApproverStatuses.Rejected]: '#e88800',
    [MemoApproverStatuses.Terminated]: '#df3131',
    [MemoApproverStatuses.Canceled]: '#007aff',
  };
  language = 'en';
  lineSecondHslColor: `hsl(${string})`;
  loaBadge = {
    ...Object.entries(MemoStatuses).reduce(
      (acc, [key, value]) => {
        acc[value] = key;
        return acc;
      },
      {} as { [k in MemoApproverStatus]: string },
    ),
    rejected: 'Return',
  };
  loaBadgeTh: { [k in MemoApproverStatus]?: string | null } = {
    ...MemoStatusesTh,
  };
  loaStatusIcons: { [k in MemoApproverStatus]?: string | null } = {};
  memberStatusIcons: {
    [k in MemoApproverStatus]?: string | null;
  } = {};
  memoLoaPreviews: MemoLoaPreview[] = [];

  private cdRef = inject(ChangeDetectorRef);
  private memoService = inject(MemoService);
  private translate = inject(TranslateService);
  private themeService = inject(ThemeService);
  private utils = inject(UtilsService);

  private readonly ngDestroy: Subject<void> = new Subject();

  constructor() {
    this.initialMemberStatusIcons();
    this.loaStatusIcons = {
      ...this.memberStatusIcons,
      approved: 'assets/images/icons/marks/check.svg#check',
      canceled: null,
      draft: null,
      pending: null,
    };
    this.lineSecondHslColor = `hsl(${this.themeService.sourceColors['primary'].h}, 47%, 93%)`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['memoId']) {
      const id = changes['memoId'].currentValue;
      if (id == undefined) {
        console.error(
          'The "memoId" is undefined. LoaCheckpointsComponent need the id to fetch the LOA preview data',
        );
      } else {
        this.fetchMemoLoaPreview(this.memoId);
      }
    }
    if (changes['loaStatusColors']) {
      const colors = changes['loaStatusColors'].currentValue;
      if (!colors || colors === 'theme') {
        this._loaStatusColors = this.themeLoaStatusColors;
      } else if (colors === 'default') {
        this._loaStatusColors = this.defaultLoaStatusColors;
      } else {
        this._loaStatusColors = colors;
      }
    }
  }

  ngOnInit(): void {
    this.language = this.translate.getCurrentLang();
    this.subscribeLanguageChange();
    if (!this.loaStatusColors || this.loaStatusColors === 'theme') {
      this._loaStatusColors = this.themeLoaStatusColors;
    }
  }

  ngOnDestroy(): void {
    this.ngDestroy.next();
    this.ngDestroy.complete();
  }

  fetchMemoLoaPreview(id: number) {
    this.memoService.getMemoLoaPreview(id).subscribe({
      next: (res) => {
        this.memoLoaPreviews = this.filters
          ? (_.filter(res, this.filters) as MemoLoaPreview[])
          : res;
        this.afterPreviewLoaFetch.emit([
          ...this.memoLoaPreviews.map((item) => _.cloneDeep(item)),
        ]);
        this.cdRef.detectChanges();
      },
      error: (err) => {
        console.error(
          'There was an error while retrieving the LOA preview data',
          err,
        );
      },
    });
  }

  initialMemberStatusIcons(): void {
    this.memberStatusIcons = {
      ...MemoStatusIconPaths,
      [MemoApproverStatuses.Canceled]:
        'assets/images/icons/time/clock-three.svg',
    };
    const keys = Object.keys(
      this.memberStatusIcons,
    ) as MemoApproverStatus[];
    keys.forEach((key) => {
      const path = this.memberStatusIcons[key];
      if (!path) {
        return;
      }
      this.memberStatusIcons[key] =
        this.utils.appendTagFromSvgPath(path);
    });
  }

  isShowProgress(status: MemoApproverStatus) {
    return !['rejected', 'terminated', 'expired'].includes(status);
  }

  isShowReason(status: MemoApproverStatus) {
    return ['rejected', 'terminated'].includes(status);
  }

  noChangeLoaMemberRemind(memberId: number) {
    const index: number = this.loaMemberIdArray.indexOf(memberId);
    if (index !== -1) {
      this.loaMemberIdArray.splice(index, 1);
    } else {
      this.loaMemberIdArray.push(memberId);
    }
    this.emitIndexLoaArray.emit(this.loaMemberIdArray);
  }

  subscribeLanguageChange(): void {
    this.translate.onLangChange
      .pipe(takeUntil(this.ngDestroy))
      .subscribe({
        next: (event: LangChangeEvent) => {
          this.language = event.lang;
          this.fetchMemoLoaPreview(this.memoId);
        },
      });
  }

  get defaultLoaStatusColors(): {
    [k in MemoApproverStatus]?: Partial<GroundColor>;
  } {
    return {
      [MemoApproverStatuses.Approved]: {
        background: '#1958d3',
        foreground: 'white',
      },
      [MemoApproverStatuses.Expired]: {
        background:
          MemoStatusColors[MemoApproverStatuses.Expired]?.foreground,
        foreground: 'white',
      },
      [MemoApproverStatuses.Pending]: {
        background:
          MemoStatusColors[MemoApproverStatuses.Pending]?.background,
        foreground:
          MemoStatusColors[MemoApproverStatuses.Pending]?.foreground,
      },
      [MemoApproverStatuses.Rejected]: {
        background:
          MemoStatusColors[MemoApproverStatuses.Rejected]?.foreground,
        foreground: 'white',
      },
      [MemoApproverStatuses.Terminated]: {
        background:
          MemoStatusColors[MemoApproverStatuses.Terminated]
            ?.foreground,
        foreground: 'white',
      },
      canceled: {
        background:
          MemoStatusColors[MemoApproverStatuses.Draft]?.background,
        foreground: '#55596D',
      },
      not_started: {
        background:
          MemoStatusColors[MemoApproverStatuses.Draft]?.background,
        foreground: '#55596D',
      },
    };
  }

  get themeLoaStatusColors(): {
    [k in MemoApproverStatus]?: Partial<GroundColor>;
  } {
    return {
      ...this.defaultLoaStatusColors,
      [MemoApproverStatuses.Approved]: {
        background: '#258D3F',
        foreground: 'white',
      },
      [MemoApproverStatuses.Pending]: {
        background: '#E8EEFB',
        foreground: '#1750C0',
      },
    };
  }
}

export type MemberStatusColor = {
  [k in MemoApproverStatus]?: Partial<GroundColor>;
};
